// What the VA side of handoffs says (spec C6, S15 batch 3; the Renewals and Record boards): the Hand
// to AE dialog, the Awaiting AE tag and its tooltip, the waiting banner with Cancel handoff, and the
// read-only line once an AE has it. Pure, so it is tested without a browser.
import type { FetchView, HandoffReason, OpenHandoff, RenewalRow } from '../auth/api';
import { managesDepartment, type Role } from '../roles';
import { isOverdue, listDate, nyToday, stageOf, when } from './renewal-lines';

export const handoffReasonLabels: Record<HandoffReason, string> = {
  quote: 'Quote',
  increase: 'Increase',
  non_renewal: 'Non-renewal',
  noc: 'NOC',
  payment_not_received: 'Payment not received',
  not_on_carrier_site: 'Not on the carrier site',
  other: 'Other',
};

/** The longest "Other" text. Must match otherTextMaxLength in apps/server/src/handoffs.ts. */
export const otherTextMax = 300;

/** An AE passed it on: the handoff was sent by someone other than the person it returns to. */
const isPass = (h: OpenHandoff) => h.kind === 'handoff' && h.createdBy.id !== (h.from?.id ?? null);

/**
 * The tag next to the status while the AE has not answered: "Awaiting AE", or "Overdue" in red past
 * the Act by day. Null once accepted (With AE) or without a handoff.
 */
export function awaitingTag(
  h: OpenHandoff | null,
  now: Date,
  meId: string,
): { label: string; cls: string; tip: string } | null {
  if (!h || h.state !== 'waiting') return null;
  const over = isOverdue(h, now);
  const actBy = h.actBy ? listDate(h.actBy) : null;
  const ae = h.to.id === meId ? null : h.to.name;
  const by = h.createdBy.id === meId ? 'you' : h.createdBy.name;
  let tip: string;
  if (over) {
    tip = `${ae ? `Awaiting ${ae}.` : 'Waiting for your answer.'} Act by ${actBy}, past due.`;
  } else if (!ae) {
    tip = `Waiting for your answer.${actBy ? ` Act by ${actBy}.` : ''}`;
  } else if (h.kind === 'reassign_to_ae') {
    tip = `Sent to ${ae} for approval by ${by}.${actBy ? ` Act by ${actBy}.` : ''}`;
  } else if (isPass(h)) {
    tip = `Passed to ${ae} by ${by}.${actBy ? ` Act by ${actBy}.` : ''}`;
  } else {
    const why = h.reason ? `, ${handoffReasonLabels[h.reason]}` : '';
    tip = `Handed to ${ae}${why}.${actBy ? ` Act by ${actBy}.` : ''}`;
  }
  return { label: over ? 'Overdue' : 'Awaiting AE', cls: over ? 'tag t-red' : 'tag awt', tip };
}

/** "today at 10:14", "Nov 10 at 09:02": the Record board's "Sent today at 10:14". */
export const sentWhen = (iso: string, now: Date) =>
  when(iso, now).replace(/ (\d\d:\d\d)$/, ' at $1');

/** The Record's waiting banner: who it waits on, when and by whom it was sent. */
export function waitingText(h: OpenHandoff, now: Date, meId: string): string {
  const by = h.createdBy.id === meId ? 'you' : h.createdBy.name;
  return `Waiting for ${h.to.name} to accept. Sent ${sentWhen(h.createdAt, now)} by ${by}.`;
}

/**
 * May cancel the waiting handoff (the server agrees): who sent it, the Manager or the Admin. The AE
 * it waits on answers it instead.
 */
export const mayCancel = (h: OpenHandoff, meId: string, manages: boolean) =>
  h.state === 'waiting' && (manages || h.createdBy.id === meId);

/** Cancel handoff's confirm popup (the Record board). */
export function cancelCopy(
  h: OpenHandoff,
  holder: { id: string; name: string } | null,
  meId: string,
): { title: string; line: string } {
  const who = !holder ? 'the Unassigned pool' : holder.id === meId ? 'you' : holder.name;
  return {
    title: `Cancel the handoff to ${h.to.name}?`,
    line: `It stays with ${who} at the same step.`,
  };
}

export const cancelledText = (h: OpenHandoff) => `Handoff to ${h.to.name} cancelled.`;

/** Hand to AE's confirm popup and toast (the Record board). */
export const handConfirm = (insured: string, ae: string) => ({
  title: `Hand ${insured} to ${ae}?`,
  line: `Waits for ${ae} to accept.`,
});
export const handedText = (insured: string, ae: string) =>
  `${insured} handed to ${ae}. Awaiting AE until they accept.`;

/** Why Hand to AE could not be sent, from the server's answer. */
export function handFailedText(status: number, error: string | undefined): string {
  if (error === 'has_open_handoff') return 'It already waits on an AE. Read the Record again.';
  if (error === 'renewal_closed') return 'This renewal is closed. Reopen it first.';
  if (status === 409) return 'Someone changed this renewal. Read the Record again.';
  if (error === 'bad_person') return 'That account executive cannot take handoffs now.';
  return 'Could not hand it to the AE. Try again.';
}

/**
 * The Record's line while an AE has the renewal (the Record board): for the person who sent it, read
 * only until it comes back, also while the AE who accepted it passes it on; null when it is not with
 * an AE or the viewer works it.
 */
export function withAeText(
  h: OpenHandoff | null,
  meId: string,
  holder: { id: string; name: string } | null,
): string | null {
  if (!h || h.kind === 'take_over' || h.from?.id !== meId || holder?.id === meId) return null;
  if (h.state === 'waiting') {
    return `With ${holder?.name ?? 'an AE'}, passed to ${h.to.name}. Steps unlock when it is returned.`;
  }
  const since = h.answeredAt ? ` since ${listDate(nyToday(new Date(h.answeredAt)))}` : '';
  return `With ${h.to.name}${since}. Steps unlock when it is returned.`;
}

/**
 * May work the renewal from a list or the Quick view: the Manager and the Admin any, the Renewal VA
 * their own (a renewal they sent to an AE who accepted it is read only for them, spec C6).
 */
export const worksRow = (r: Pick<RenewalRow, 'handledBy'>, role: Role, meId: string) =>
  managesDepartment(role) || (role === 'Renewal VA' && r.handledBy?.id === meId);

/** Hand to AE is offered: a renewal they work, open, with no handoff in play. */
export const offersHand = (
  r: Pick<RenewalRow, 'handledBy' | 'status' | 'handoff'>,
  role: Role,
  meId: string,
) => worksRow(r, role, meId) && stageOf(r) !== 'closed' && !r.handoff;

/** "9 days before renewal", "1 day before renewal", "on the renewal date", "2 days after renewal". */
function beforeRenewal(days: number): string {
  if (days === 0) return 'on the renewal date';
  const n = Math.abs(days);
  return `${n} ${n === 1 ? 'day' : 'days'} ${days > 0 ? 'before' : 'after'} renewal`;
}

/**
 * Rachy's two one-click hands (the Record board, Nov 1): a banner under the header for whoever works
 * the record, never automatic. Need payment with 10 days or fewer left: the AE follows up on the
 * payment. Not on the carrier site: the last fetch found no renewal there and no dec page is in, from
 * the days before renewal set in Settings (the step's "call the carrier" rule, 20 on the board): the
 * AE calls the carrier (Codex on PR 309: the carrier fetch decides, not the AMS360 recheck). The
 * button opens Hand to AE with the reason and a note filled in. Null otherwise.
 */
export function oneClickHand(
  r: Pick<RenewalRow, 'handledBy' | 'status' | 'handoff' | 'daysLeft'>,
  role: Role,
  meId: string,
  /** The Dec page step's fetch found no renewal on the carrier site, and the call is due. */
  notOnSite = false,
): { line: string; button: string; reason: HandoffReason; note: string } | null {
  if (!offersHand(r, role, meId)) return null;
  const when = beforeRenewal(r.daysLeft);
  if (r.status === 'need_payment' && r.daysLeft <= 10) {
    return {
      line: `Payment not received ${when}. The AE follows up with the bank or the insured.`,
      button: 'Hand to AE: payment not received',
      reason: 'payment_not_received',
      note: `Payment not received ${when}. Carrier site checked.`,
    };
  }
  if (notOnSite) {
    return {
      line: `Not on the carrier site ${when}. The AE calls the carrier.`,
      button: 'Hand to AE: not on the carrier site',
      reason: 'not_on_carrier_site',
      note: `Renewal not on the carrier site ${when}. Please call the carrier.`,
    };
  }
  return null;
}

/** The fetch's "not on the carrier site yet", once the call to the carrier is due (Settings). */
export const notOnCarrierSite = (f: FetchView | null) =>
  !!f &&
  f.job?.status === 'ok' &&
  f.job.notAvailable &&
  !f.documents.some((d) => d.kind === 'dec_page') &&
  f.followUp.due;
